// VideoPreviewDialog — responsive modal that renders an embedded video
// inside a 16:9 iframe. Triggered from the small video icon button in
// the top-left corner of every property card on the homepage's "Our
// Collection" section.
//
// Behaviour:
// - Locks body scroll while open + closes on Escape / backdrop click.
// - The iframe URL is hard-coded for now (per the brand's current
//   sendspark preview reel). Replace with a per-property prop when each
//   property has its own video.
// - All click handlers stopPropagation + preventDefault so the dialog
//   never triggers the surrounding card-wide <Link>.

import React, { useEffect } from "react";
import { X } from "lucide-react";

const VIDEO_SRC = "https://sendspark.com/embed/437kaiceb9x9sqaxxbrb316oa5lve8n2";

const VideoPreviewDialog = ({ title, onClose, testIdPrefix }) => {
  // Lock body scroll + close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Video — ${title}` : "Property video"}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1A1513]/85 backdrop-blur-sm px-3 sm:px-6 py-6 sm:py-10"
      data-testid={`${testIdPrefix}-video-dialog`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-[#FDFBF7] text-[#2C2621] hover:text-[#C16542] border border-[#D4A373]/35 shadow-[0_10px_30px_-6px_rgba(0,0,0,0.45)] transition-colors"
          aria-label="Close video"
          data-testid={`${testIdPrefix}-video-close`}
        >
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>

        {/* Optional title strip */}
        {title ? (
          <p
            className="mb-3 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#D4A373] text-center"
            data-testid={`${testIdPrefix}-video-title`}
          >
            {title}
          </p>
        ) : null}

        {/* 16:9 responsive iframe wrapper */}
        <div
          style={{ position: "relative", height: 0, width: "100%", paddingBottom: "56.25%" }}
          className="bg-[#1A1513] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
        >
          <iframe
            title={title ? `${title} video` : "Property video"}
            src={VIDEO_SRC}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ position: "absolute", width: "100%", height: "100%", border: 0, borderRadius: "6px", left: 0, top: 0 }}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPreviewDialog;
